import type {
  Program as ProgramT,
  ProgramProgress as ProgramProgressT,
} from "./schemas/program.js";
import { sessionHasEntriesForWeek } from "./programResolution.js";

// Pure program-scheduling math shared by every surface that proposes
// "next up" for a followed program (mobile Plan tab + hero, the lifted
// ProgramsPlanView). Moved from apps/mobile screens/programsMath.ts in
// the Follow+Publish lift.

// Auto-advance contract: the server upserts PROGPROG#<programId> on
// session save when fromPlanId matches `prog/<id>/wk<n>/sess<m>`.
// nextSlotFor computes Day N+1 on next mount, skipping (week, session)
// cells that resolve to zero entries — a slot can be inactive on a
// subset of weeks (sets: [], scheduler-composer blocks) or cadence-
// skipped, and proposing such a cell would start an empty workout.
// End-of-cycle (or nothing active ahead) clamps so a finished cycle
// stays visible until a fresh one is started manually.
export const nextSlotFor = (
  program: ProgramT,
  progress: ProgramProgressT | null,
): { weekIdx: number; sessionIdx: number } => {
  const sessionsPerCycle = program.sessions.length;
  const cycleWeeks = program.cycleWeeks;
  // Linear (week, session) index of the first candidate cell.
  const start = progress
    ? progress.lastWeekIdx * sessionsPerCycle + progress.lastSessionIdx + 1
    : 0;
  const total = cycleWeeks * sessionsPerCycle;
  for (let i = start; i < total; i++) {
    const weekIdx = Math.floor(i / sessionsPerCycle);
    const sessionIdx = i % sessionsPerCycle;
    if (sessionHasEntriesForWeek(program.sessions[sessionIdx]!, weekIdx)) {
      return { weekIdx, sessionIdx };
    }
  }
  // Nothing active ahead — preserve the pre-skip clamp behaviour
  // (finished cycle pins to its last computed slot).
  if (!progress) return { weekIdx: 0, sessionIdx: 0 };
  let nextSession = progress.lastSessionIdx + 1;
  let nextWeek = progress.lastWeekIdx;
  if (nextSession >= sessionsPerCycle) {
    nextSession = 0;
    nextWeek = progress.lastWeekIdx + 1;
  }
  if (nextWeek >= cycleWeeks) nextWeek = cycleWeeks - 1;
  return { weekIdx: nextWeek, sessionIdx: nextSession };
};
